<template>
  <div class="shop-page">
    <div class="shop-hero" />

    <main
      v-if="shopReady"
      class="shop-body"
    >
      <section class="store-card">
        <div class="store-head">
          <img
            class="store-logo"
            :src="shopImage"
            alt=""
            @click="openLogoDialog"
            @error="logoError = true"
          >
          <div class="store-info">
            <div class="store-title">
              <span>{{ shop.name || '--' }}</span>
              <em>{{ $t('shopCenter.verified') }}</em>
            </div>
            <div class="credit-row">
              <span>{{ shop.credit || 0 }}</span>
              <van-icon
                v-for="index in 5"
                :key="index"
                name="star"
                :class="{ active: index <= starCount }"
              />
            </div>
          </div>
        </div>
        <div class="stats-row">
          <div
            v-for="item in stats"
            :key="item.label"
            class="stat-item"
          >
            <strong>{{ item.value }}</strong>
            <span>{{ $t(item.label) }}</span>
          </div>
        </div>
      </section>

      <section
        v-if="Number(shop.speed) === 1"
        class="speed-row "
      >
        <span>{{ speedText }}</span>
        <div class="loading-bar accelerate" />
      </section>

      <section class="quick-card">
        <button
          v-for="item in quickItems"
          :key="item.label"
          type="button"
          class="quick-item"
          @click="handleQuick(item)"
        >
          <van-icon :name="item.icon" />
          <span>{{ $t(item.label) }}</span>
        </button>
      </section>

      <section class="panel-card">
        <h2>{{ $t('shopCenter.myOrders') }}</h2>
        <div class="order-grid">
          <div
            v-for="item in orderItems"
            :key="item.label"
            class="order-item"
            @click="goOrders(item)"
          >
            <em v-if="orderCount(item.stat)">{{ orderCount(item.stat) }}</em>
            <van-icon :name="item.icon" />
            <span>{{ $t(item.label) }}</span>
          </div>
        </div>
      </section>

      <section class="panel-card">
        <h2>{{ $t('shopCenter.funds') }}</h2>
        <div class="fund-grid">
          <div>
            <strong>{{ utils_moneyMark() }} {{ formatMoney(funds.trading) }}</strong>
            <span>{{ $t('shopCenter.trading') }}</span>
          </div>
          <div>
            <strong>{{ utils_moneyMark() }} {{ formatMoney(funds.balance || user.money) }}</strong>
            <span>{{ $t('shopCenter.balance') }}</span>
          </div>
        </div>
      </section>

      <section class="intro-card">
        {{ $t('shopCenter.intro') }}
      </section>
    </main>

    <van-popup
      v-model="logoDialog"
      class="logo-popup"
      round
      closeable
      close-icon="cross"
    >
      <div class="logo-dialog">
        <h3>{{ $t('shopCenter.logoTitle') }}</h3>
        <van-uploader
          v-model="logoFileList"
          :max-count="1"
          :max-size="2097152"
          :before-read="beforeLogoRead"
          :after-read="afterLogoRead"
          @oversize="onLogoOversize"
          @delete="onLogoDelete"
        >
          <div class="logo-upload-box">
            <van-icon name="photograph" />
          </div>
        </van-uploader>
        <van-button
          class="logo-save"
          :loading="logoSaving"
          :disabled="logoSaving"
          loading-type="spinner"
          type="danger"
          @click="saveLogo"
        >
          {{ $t('shopCenter.logoSave') }}
        </van-button>
        <p>{{ $t('shopCenter.logoSizeTip') }}</p>
      </div>
    </van-popup>
  </div>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import Api from '../../interface/index';
import Fetch from '../../utils/fetch';
import { Button, Dialog, Icon, Popup, Toast, Uploader } from 'vant';

Vue.use(Icon).use(Dialog).use(Toast).use(Popup).use(Uploader).use(Button);

export default {
  name: 'Shop',
  data() {
    return {
      app: {},
      user: {},
      shop: {},
      orderCounts: {},
      funds: {
        trading: '0.00',
        balance: '0.00',
      },
      shopReady: false,
      logoError: false,
      logoFallback: require('../images/shop2/thumbnail.png'),
      logoDialog: false,
      logoFileList: [],
      logoFile: null,
      logoSaving: false,
      quickItems: [
        { label: 'shopCenter.myStore', icon: 'wap-home-o' },
        { label: 'shopCenter.supply', icon: 'apps-o' },
        { label: 'shopCenter.aboutStore', icon: 'info-o' },
      ],
      orderItems: [
        { label: 'shopCenter.waitDeliver', icon: 'logistics', stat: 0 },
        { label: 'shopCenter.waitStay', icon: 'underway-o', stat: 1 },
        { label: 'shopCenter.staying', icon: 'exchange', stat: 2 },
        { label: 'shopCenter.completed', icon: 'bag-o', stat: 4 },
      ],
    };
  },
  computed: {
    shopImage() {
      if (this.logoError) {
        return this.logoFallback;
      }
      return this.buildImageUrl(this.shop.img, this.logoFallback);
    },
    starCount() {
      var credit = Number(this.shop.credit || 0);
      if (!isFinite(credit) || credit <= 0) return 0;
      return Math.max(1, Math.min(5, Math.ceil(credit / 20)));
    },
    stats() {
      return [
        { label: 'shopCenter.goodsNum', value: Number(this.shop.goods || 0) },
        { label: 'shopCenter.orderNum', value: Number(this.shop.orders || 0) },
        { label: 'shopCenter.visitors', value: Number(this.shop.views || 0) },
        { label: 'shopCenter.favorites', value: Number(this.shop.fav || 0) },
      ];
    },
    speedText() {
      return Number(this.shop.speed) === 1 ? this.$t('shopCenter.speeding') : this.$t('shopCenter.noSpeed');
    },
  },
  created() {
    if (this.$parent && this.$parent.footer) {
      this.$parent.footer('shop', true);
    }
  },
  mounted() {
    this.start();
  },
  methods: {
    start() {
      Fetch('/index/webconfig').then((r) => {
        this.app = r.data || {};
      });
      Fetch('/user/shop_info', {}, null, true).then((r) => {
        var data = r.data || {};
        this.user = data.user || {};
        this.shop = data.shop || {};
        this.orderCounts = data.order_counts || (this.shop && this.shop.order_counts) || {};
        this.funds = data.funds || (this.shop && this.shop.funds) || this.funds;
        this.logoError = false;
        this.routeByStatus();
      });
    },
    routeByStatus() {
      if (!this.shop || !this.shop.id) {
        this.$router.replace('/aplyshop');
        return;
      }
      var stat = Number(this.shop.stat);
      if (stat === 1) {
        this.shopReady = true;
        return;
      }
      var message = this.$t('shopCenter.pendingTip');
      if (stat === 2) {
        message = this.$t('shopCenter.frozenTip');
      }
      if (stat === 3) {
        message = this.$t('shopCenter.pausedTip');
      }
      Dialog.alert({
        title: this.$t('utils.prompt'),
        message,
        confirmButtonText: this.$t('utils.confirm'),
      }).then(() => {
        this.$router.replace('/user');
      });
    },
    openLogoDialog() {
      this.logoDialog = true;
      this.logoFile = null;
      this.logoFileList = [];
    },
    beforeLogoRead(file) {
      var type = String(file.type || '').toLowerCase();
      var name = String(file.name || '').toLowerCase();
      var extValid = /\.(jpg|jpeg|png)$/.test(name);
      if (!extValid || ['image/jpeg', 'image/png'].indexOf(type) === -1) {
        Toast(this.$t('shopCenter.logoTypeError'));
        return false;
      }
      if (file.size > 2 * 1024 * 1024) {
        Toast(this.$t('shopCenter.logoSizeError'));
        return false;
      }
      return true;
    },
    afterLogoRead(file) {
      this.logoFile = file.file || null;
    },
    onLogoOversize() {
      Toast(this.$t('shopCenter.logoSizeError'));
    },
    onLogoDelete() {
      this.logoFile = null;
    },
    saveLogo() {
      if (!this.logoFile) {
        Toast(this.$t('shopCenter.logoRequired'));
        return;
      }
      if (this.logoSaving) {
        return;
      }
      var formData = new FormData();
      formData.append('file', this.logoFile);
      formData.append('language', localStorage.getItem('lang') || 'en_us');
      formData.append('token', localStorage.getItem('token') || '');
      this.logoSaving = true;
      axios.post(Api.commonUrl + '/api/user/shop_logo', formData).then((res) => {
        var response = res.data || {};
        if (Number(response.code) !== 1) {
          Toast(this.getResponseMessage(response.info, this.$t('shopCenter.logoSaveFail')));
          return;
        }
        var data = response.data || {};
        var img = data.img || data;
        if (img) {
          this.shop.img = img;
          this.logoError = false;
        }
        this.logoDialog = false;
        this.logoFile = null;
        this.logoFileList = [];
        Toast(this.getResponseMessage(response.info, this.$t('shopCenter.logoSaveSuccess')));
      }).catch(() => {
        Toast(this.$t('shopCenter.logoSaveFail'));
      }).then(() => {
        this.logoSaving = false;
      });
    },
    getResponseMessage(info, fallback) {
      var lang = localStorage.getItem('lang') || 'en_us';
      if (info && typeof info === 'object') {
        return info[lang] || info.en_us || info.zh_cn || fallback;
      }
      return info || fallback;
    },
    handleQuick(item) {
      if (item.label === 'shopCenter.myStore') {
        this.$router.push('/hotel');
        return;
      }
      if (item.label === 'shopCenter.supply') {
        this.$router.push('/shopsupply');
        return;
      }
      if (item.label === 'shopCenter.aboutStore') {
        this.$router.push('/shop/about');
      }
    },
    orderCount(stat) {
      return Number(this.orderCounts[String(stat)] || this.orderCounts[stat] || 0);
    },
    goOrders(item) {
      this.$router.push({
        path: '/shoporders',
        query: {
          stat: item.stat,
        },
      });
    },
    buildImageUrl(path, fallback) {
      if (!path) {
        return fallback;
      }
      if (/^(https?:)?\/\//.test(path) || String(path).indexOf('data:') === 0) {
        return path;
      }
      if (this.app && this.app.domain) {
        return this.app.domain.replace(/\/$/, '') + '/' + String(path).replace(/^\//, '');
      }
      return path;
    },
    formatMoney(value) {
      var number = Number(value);
      if (!isFinite(number)) {
        number = 0;
      }
      return number.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    kefuTo() {
      if (this.app && this.app.kefu_link) {
        window.location.href = this.app.kefu_link;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.shop-page {
  min-height: 100vh;
  padding-bottom: 72px;
  background: #eef7ff;
  color: #07152b;
}

.shop-hero {
  height: 60px;
  background: linear-gradient(135deg, #ffd927 0%, #ffb01f 48%, #ff8418 100%);
}

.shop-body {
  padding: 0 28px 20px;
}

.store-card,
.quick-card,
.panel-card,
.intro-card {
  border-radius: 10px;
  background: #fff;
}

.store-card {
  margin-top: -38px;
  padding: 14px 10px 12px;
}

.store-head {
  display: flex;
  align-items: center;
}

.store-logo {
  width: 64px;
  height: 64px;
  flex: 0 0 64px;
  border-radius: 9px;
  object-fit: cover;
  background: #13204a;
  cursor: pointer;
}

.store-info {
  min-width: 0;
  padding-left: 12px;
}

.store-title {
  display: flex;
  align-items: center;
  min-width: 0;
}

.store-title span {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 22px;
  font-weight: 700;
}

.store-title em {
  flex: 0 0 auto;
  margin-left: 7px;
  padding: 0 8px;
  border-radius: 4px;
  background: #050505;
  color: #fff;
  font-style: normal;
  font-size: 10px;
  line-height: 18px;
  font-weight: 700;
}

.credit-row {
  display: flex;
  align-items: center;
  margin-top: 3px;
  color: #aeb4bf;
  font-size: 14px;
  line-height: 20px;
}

.credit-row span {
  margin-right: 4px;
}

.credit-row .van-icon {
  margin-right: 2px;
  color: #e5e7eb;
  font-size: 14px;
}

.credit-row .active {
  color: #ffc400;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding-top: 18px;
  text-align: center;
}

.stat-item strong {
  display: block;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.stat-item span {
  display: block;
  margin-top: 12px;
  color: #334155;
  font-size: 12px;
  line-height: 16px;
}

.speed-row {
  height: 25px;
  margin: 14px 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: 17px;
  background: #fff;
  color: #4b5563;
  font-size: 12px;
  line-height: 25px;
  overflow: hidden;
}

.speed-row i {
  position: absolute;
  right: 14px;
  top: 14px;
  width: 24px;
  height: 6px;
  background: #15bfd9;
  opacity: .35;
}

.speed-row i.active {
  width: 68px;
  opacity: 1;
}

.quick-card {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 14px;
}

.quick-item {
  height: 125px;
  border: 0;
  border-radius: 8px;
  background: #f7f8fc;
  color: #172033;
}

.quick-item .van-icon {
  display: block;
  margin-bottom: 15px;
  color: #8aa9d5;
  font-size: 36px;
}

.quick-item span {
  font-size: 12px;
  line-height: 18px;
}

.panel-card {
  margin-top: 14px;
  padding: 16px 14px 20px;
}

.panel-card h2 {
  margin: 0;
  padding-bottom: 13px;
  border-bottom: 1px solid #eef0f2;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.order-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding-top: 18px;
  text-align: center;
}

.order-item .van-icon {
  display: block;
  margin-bottom: 14px;
  color: #221427;
  font-size: 28px;
}

.order-item {
  position: relative;
}

.order-item em {
  position: absolute;
  top: -8px;
  left: 50%;
  min-width: 16px;
  height: 16px;
  margin-left: 8px;
  padding: 0 4px;
  border-radius: 8px;
  background: #f71944;
  color: #fff;
  font-style: normal;
  font-size: 10px;
  line-height: 16px;
  font-weight: 700;
  box-sizing: border-box;
}

.order-item span {
  color: #4b5563;
  font-size: 12px;
  line-height: 16px;
}

.fund-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding: 15px 0 0;
  text-align: center;
}

.fund-grid strong {
  display: block;
  color: #07152b;
  font-size: 18px;
  line-height: 20px;
  font-weight: 500;
}

.fund-grid span {
  display: block;
  margin-top: 10px;
  color: #334155;
  font-size: 12px;
  line-height: 16px;
}

.intro-card {
  margin-top: 14px;
  padding: 28px 17px 38px;
  color: #172033;
  font-size: 14px;
  line-height: 30px;
}

.logo-popup {
  width: 320px;
  border-radius: 16px;
  overflow: hidden;
}

.logo-dialog {
  padding: 22px 24px 20px;
  text-align: center;
  color: #172033;
}

.logo-dialog h3 {
  margin: 0 0 18px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.logo-dialog /deep/ .van-uploader__wrapper {
  justify-content: center;
}

.logo-dialog /deep/ .van-uploader {
  display: block;
}

.logo-dialog /deep/ .van-uploader__preview,
.logo-dialog /deep/ .van-uploader__preview-image {
  width: 80px;
  height: 80px;
  margin: 0;
  border-radius: 0;
}

.logo-upload-box {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f6f7fb;
  color: #d6dbe4;
}

.logo-upload-box .van-icon {
  font-size: 24px;
}

.logo-save {
  width: 180px;
  height: 38px;
  display: block;
  margin-top: 22px;
  margin-left: auto;
  margin-right: auto;
  border: 0;
  border-radius: 10px;
  background: #ff1111;
  color: #fff;
  font-size: 14px;
  line-height: 38px;
}

.logo-save.van-button--disabled {
  opacity: .75;
}

.logo-dialog p {
  margin: 17px 0 0;
  color: #4b5563;
  font-size: 12px;
  line-height: 18px;
}

.kefu {
  position: fixed;
  right: 26px;
  bottom: 100px;
  z-index: 12;
  width: 66px;
  height: 66px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid #fff;
  background: linear-gradient(180deg, #1884ff 0%, #1884ff 68%, #f70720 68%, #f70720 100%);
  box-shadow: 0 4px 14px rgba(0, 0, 0, .22);
  box-sizing: border-box;
}

.kefu_img {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.kefu_img .van-icon {
  font-size: 36px;
}
@keyframes accelerate-move {
    0% {
        left: -50px;
        transform: scaleX(.5);
        animation-timing-function: ease-in
    }

    30% {
        left: 30%;
        transform: scaleX(.8);
        animation-timing-function: ease-in-out
    }

    60% {
        left: 70%;
        transform: scaleX(1);
        animation-timing-function: ease-out
    }

    to {
        left: 100%;
        transform: scaleX(1.2);
        animation-timing-function: linear
    }
}
.accelerate {
    animation: accelerate-move 2s infinite
}
.loading-bar{
    width: 50px;
    height: 5px;
    background: #00d4ff;
    position: absolute;
    bottom: 10px;
    left: -50px;
    transition: left .2s linear
}
</style>
